import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { COPY } from "@/config/copy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Assessment({ answers, setAnswers, onComplete, onExit }) {
  const a = COPY.assessment;
  const [config, setConfig] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  // One-shot: fetch questions on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/assessment/config`).then((r) => {
      setConfig(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="overline">{a.loading_label}</div>
      </div>
    );
  }
  if (!config) return <div className="p-16 text-center">{a.load_error}</div>;

  const questions = config.questions;
  const q = questions[idx];
  const selected = answers[String(q.id)];
  const progress = ((idx + (selected ? 1 : 0)) / questions.length) * 100;

  const choose = (aid) => setAnswers({ ...answers, [String(q.id)]: aid });
  const next = () => (idx < questions.length - 1 ? setIdx(idx + 1) : onComplete());
  const prev = () => (idx === 0 ? onExit() : setIdx(idx - 1));

  const counter = a.counter_format
    .replace("{current}", String(idx + 1).padStart(2, "0"))
    .replace("{total}", String(questions.length).padStart(2, "0"));

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <div className="progress-track" data-testid="progress-track">
        <div className="progress-fill" style={{ width: `${progress}%` }} data-testid="progress-fill" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-12 pt-10 pb-8">
        <div className="section-label">{COPY.brand.audit_label}</div>
        <div className="section-label" data-testid="question-counter">{counter}</div>
      </div>

      {/* Centered vertical question layout */}
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="stage w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <div className="overline mb-6">{q.overline}</div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.15] font-light max-w-2xl mx-auto">
                {q.question}
              </h2>

              <div className="mt-12 mx-auto max-w-xl flex flex-col gap-3">
                {q.answers.map((ans) => (
                  <button
                    key={ans.id}
                    onClick={() => choose(ans.id)}
                    className={`answer-card ${selected === ans.id ? "selected" : ""}`}
                    data-testid={`answer-q${q.id}-${ans.id}`}
                  >
                    <span className="answer-marker">{ans.id.toUpperCase()}</span>
                    <span className="answer-label">{ans.label}</span>
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-center gap-4 mt-12">
                <button className="btn-ghost" onClick={prev} data-testid="prev-btn">
                  <ArrowLeft size={14} strokeWidth={1.5} /> {idx === 0 ? a.exit_label : a.back_label}
                </button>
                <button className="btn-primary" onClick={next} disabled={!selected} data-testid="next-btn">
                  {idx === questions.length - 1 ? a.finish_label : a.next_label}
                  <ArrowRight className="btn-arrow" size={16} strokeWidth={1.5} />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
